import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Package } from "lucide-react";
import { useProducts } from "@/hooks/useProducts";
import { Checkbox } from "@/components/ui/checkbox";

interface Bundle {
  id: string;
  name: string;
  slug: string;
  description: string;
  discount_percentage: number;
  product_ids: any;
  is_active: boolean;
  display_order: number;
}

export const BundlesManagement = () => {
  const [bundles, setBundles] = useState<Bundle[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingBundle, setEditingBundle] = useState<Bundle | null>(null);
  const { products } = useProducts();

  const [formData, setFormData] = useState({
    name: "",
    slug: "",
    description: "",
    discount_percentage: 15,
    product_ids: [] as number[],
    is_active: true,
    display_order: 0,
  });

  useEffect(() => {
    fetchBundles();
  }, []);

  const fetchBundles = async () => {
    try {
      const { data, error } = await supabase
        .from("bundles")
        .select("*")
        .order("display_order");

      if (error) throw error;
      setBundles(data || []);
    } catch (error) {
      toast.error("Fout bij ophalen bundles");
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      if (editingBundle) {
        const { error } = await supabase
          .from("bundles")
          .update({
            name: formData.name,
            slug: formData.slug,
            description: formData.description,
            discount_percentage: formData.discount_percentage,
            product_ids: formData.product_ids,
            is_active: formData.is_active,
            display_order: formData.display_order,
          })
          .eq("id", editingBundle.id);

        if (error) throw error;
        toast.success("Bundle bijgewerkt!");
      } else {
        const { error } = await supabase.from("bundles").insert([{
          name: formData.name,
          slug: formData.slug,
          description: formData.description,
          discount_percentage: formData.discount_percentage,
          product_ids: formData.product_ids,
          is_active: formData.is_active,
          display_order: formData.display_order,
        }]);

        if (error) throw error;
        toast.success("Bundle toegevoegd!");
      }

      resetForm();
      setIsDialogOpen(false);
      fetchBundles();
    } catch (error) {
      toast.error("Fout bij opslaan bundle");
      console.error(error);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm("Weet je zeker dat je deze bundle wilt verwijderen?")) return;

    try {
      const { error } = await supabase.from("bundles").delete().eq("id", id);
      if (error) throw error;
      toast.success("Bundle verwijderd!");
      fetchBundles();
    } catch (error) {
      toast.error("Fout bij verwijderen bundle");
      console.error(error);
    }
  };

  const handleEdit = (bundle: Bundle) => {
    setEditingBundle(bundle);
    const productIds = Array.isArray(bundle.product_ids) 
      ? bundle.product_ids.map((id: any) => typeof id === 'string' ? parseInt(id) : id)
      : [];
    setFormData({
      name: bundle.name,
      slug: bundle.slug,
      description: bundle.description || "",
      discount_percentage: bundle.discount_percentage,
      product_ids: productIds,
      is_active: bundle.is_active,
      display_order: bundle.display_order,
    });
    setIsDialogOpen(true);
  };

  const resetForm = () => {
    setFormData({
      name: "",
      slug: "",
      description: "",
      discount_percentage: 15,
      product_ids: [] as number[],
      is_active: true,
      display_order: 0,
    });
    setEditingBundle(null);
  };

  const toggleProductSelection = (productId: number) => {
    setFormData(prev => ({
      ...prev,
      product_ids: prev.product_ids.includes(productId)
        ? prev.product_ids.filter(id => id !== productId)
        : [...prev.product_ids, productId]
    }));
  };

  if (isLoading) {
    return <div>Laden...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-3xl font-bold">Bundles Beheer</h2>
        <Dialog open={isDialogOpen} onOpenChange={(open) => {
          setIsDialogOpen(open);
          if (!open) resetForm();
        }}>
          <DialogTrigger asChild>
            <Button className="btn-hero">
              <Plus className="w-4 h-4 mr-2" />
              Nieuwe Bundle
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {editingBundle ? "Bundle Bewerken" : "Nieuwe Bundle"}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="name">Naam *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                />
              </div>

              <div>
                <Label htmlFor="slug">Slug *</Label>
                <Input
                  id="slug"
                  value={formData.slug}
                  onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                  placeholder="bijv: complete-ritual-bundle"
                  required
                />
              </div>

              <div>
                <Label htmlFor="description">Beschrijving</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={3}
                />
              </div>

              <div>
                <Label htmlFor="discount">Korting Percentage (%)</Label>
                <Input
                  id="discount"
                  type="number"
                  min="0"
                  max="100"
                  value={formData.discount_percentage}
                  onChange={(e) => setFormData({ ...formData, discount_percentage: parseFloat(e.target.value) })}
                  required
                />
              </div>

              <div>
                <Label htmlFor="display_order">Volgorde</Label>
                <Input
                  id="display_order"
                  type="number"
                  value={formData.display_order}
                  onChange={(e) => setFormData({ ...formData, display_order: parseInt(e.target.value) })}
                />
              </div>

              <div>
                <Label>Selecteer Producten *</Label>
                <div className="space-y-2 mt-2 border rounded-lg p-4 max-h-60 overflow-y-auto">
                  {products.filter(p => !p.isAccessory).map(product => (
                    <div key={product.id} className="flex items-center space-x-2">
                      <Checkbox
                        id={String(product.id)}
                        checked={formData.product_ids.includes(product.id)}
                        onCheckedChange={() => toggleProductSelection(product.id)}
                      />
                      <label htmlFor={String(product.id)} className="text-sm cursor-pointer flex-1">
                        {product.name} - {product.price25g}
                      </label>
                    </div>
                  ))}
                </div>
                <p className="text-sm text-muted-foreground mt-1">
                  Geselecteerd: {formData.product_ids.length} producten
                </p>
              </div>

              <div className="flex items-center space-x-2">
                <Checkbox
                  id="is_active"
                  checked={formData.is_active}
                  onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked as boolean })}
                />
                <Label htmlFor="is_active">Actief</Label>
              </div>

              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                  Annuleren
                </Button>
                <Button type="submit" className="btn-hero">
                  {editingBundle ? "Bijwerken" : "Toevoegen"}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid gap-4">
        {bundles.length === 0 ? (
          <Card>
            <CardContent className="pt-6 text-center text-muted-foreground">
              <Package className="w-12 h-12 mx-auto mb-2 opacity-50" />
              <p>Nog geen bundles. Voeg je eerste bundle toe!</p>
            </CardContent>
          </Card>
        ) : (
          bundles.map((bundle) => (
            <Card key={bundle.id}>
              <CardHeader>
                <div className="flex justify-between items-start">
                  <div className="space-y-1">
                    <CardTitle className="flex items-center gap-2">
                      {bundle.name}
                      {!bundle.is_active && (
                        <Badge variant="secondary">Inactief</Badge>
                      )}
                      <Badge variant="outline">-{bundle.discount_percentage}%</Badge>
                    </CardTitle>
                    <p className="text-sm text-muted-foreground">{bundle.slug}</p>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => handleEdit(bundle)}
                    >
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="destructive"
                      size="icon"
                      onClick={() => handleDelete(bundle.id)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {bundle.description && (
                  <p className="text-sm mb-3">{bundle.description}</p>
                )}
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Package className="w-4 h-4" />
                  {Array.isArray(bundle.product_ids) ? bundle.product_ids.length : 0} producten in bundle
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>
    </div>
  );
};
